import React, { useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  format,
} from 'date-fns';
import { MemberAvailabilityBlock } from '../../types/availability';

interface DaySummary {
  date: Date;
  availableCount: number;
  bookedCount: number;
  totalHours: number;
}

interface MonthCalendarGridProps {
  currentDate: Date;
  availabilityData: MemberAvailabilityBlock[];
  onDayClick: (date: Date) => void;
}

const MonthCalendarGrid: React.FC<MonthCalendarGridProps> = ({
  currentDate,
  availabilityData,
  onDayClick,
}) => {
  // Calculate calendar grid boundaries
  const gridDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentDate]);

  // Calculate summary for each day
  const daySummaries = useMemo(() => {
    const summaries: Record<string, DaySummary> = {};

    gridDays.forEach(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      // Exclude breaks — they're non-bookable and shouldn't inflate the day's
      // available count or total hours (they're rendered elsewhere as breaks).
      const dayBlocks = availabilityData.filter(
        block => block.date === dateKey && block.block_type !== 'break'
      );

      const availableCount = dayBlocks.filter(b => !b.is_booked).length;
      const bookedCount = dayBlocks.filter(b => b.is_booked).length;
      const totalHours = dayBlocks.reduce((sum, b) => {
        // Calculate hours from start_time and end_time
        const [startH, startM] = b.start_time.split(':').map(Number);
        const [endH, endM] = b.end_time.split(':').map(Number);
        const hours = (endH - startH) + (endM - startM) / 60;
        return sum + hours;
      }, 0);

      summaries[dateKey] = {
        date: day,
        availableCount,
        bookedCount,
        totalHours,
      };
    });

    return summaries;
  }, [gridDays, availabilityData]);

  // Determine day cell color
  const getDayColor = (summary: DaySummary, isCurrentMonth: boolean): string => {
    if (!isCurrentMonth) return 'bg-clinic-cream/30 text-clinic-muted/50';
    if (summary.availableCount === 0 && summary.bookedCount === 0) {
      return 'bg-white text-clinic-muted'; // No availability set
    }
    if (summary.availableCount === 0 && summary.bookedCount > 0) {
      return 'bg-clinic-gold/10 text-clinic-gold'; // All booked
    }
    return 'bg-clinic-primary/10 text-clinic-primary'; // Has availability
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="border border-clinic-primary-lighter rounded-card overflow-hidden">
      {/* Week day headers */}
      <div className="grid grid-cols-7 bg-clinic-cream border-b border-clinic-primary-lighter">
        {weekDays.map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-clinic-dark">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {gridDays.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const summary = daySummaries[dateKey];
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());

          return (
            <button
              key={dateKey}
              onClick={() => isCurrentMonth && onDayClick(day)}
              disabled={!isCurrentMonth}
              className={`
                p-2 min-h-[60px] md:min-h-[80px] border-b border-r border-clinic-primary-lighter/50 text-left transition-colors
                ${getDayColor(summary, isCurrentMonth)}
                ${isCurrentMonth ? 'hover:bg-opacity-75 cursor-pointer' : 'cursor-default'}
                ${isToday ? 'ring-2 ring-clinic-primary ring-inset' : ''}
              `}
            >
              {/* Day number */}
              <div className={`text-sm font-medium ${isToday ? 'text-clinic-primary' : ''}`}>
                {format(day, 'd')}
              </div>

              {/* Summary - only for current month days with data */}
              {isCurrentMonth && (summary.availableCount > 0 || summary.bookedCount > 0) && (
                <div className="mt-1 text-xs space-y-0.5">
                  {summary.availableCount > 0 && (
                    <div className="text-clinic-primary">
                      {summary.availableCount} avail
                    </div>
                  )}
                  {summary.bookedCount > 0 && (
                    <div className="text-clinic-gold">
                      {summary.bookedCount} booked
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendarGrid;
